import { FC } from 'react'

import {
  GreyGrey60,
  Primary13Regular,
  Skeleton,
  Text
} from '@sequencehq/core-components'

import { useTemplateResolver } from '../../hooks'
import { getReminderEmail } from '../../helpers'
import type { ReminderEmailData } from '../../Reminder.executionSummary.hooks'
import type { Config } from '../..'

// The resolved line's height, so it doesn't pop in and shift the body preview
const SKELETON_WIDTH = '180px'
const SKELETON_HEIGHT = '16px'

/**
 * The inspector renders reminders as a body-only preview, which drops the email
 * card's recipients row — so the addresses go under the timestamp instead.
 */
export const ReminderEmailRecipients: FC<{
  config: Pick<Config, 'to' | 'cc' | 'bcc' | 'subject' | 'template'>
  emailData: ReminderEmailData
}> = ({ config, emailData }) => {
  const { resolveTemplate, isPending } = useTemplateResolver({
    invoice: emailData.invoice,
    customer: emailData.customer
  })

  const { to } = getReminderEmail({ config, emailData })

  if (to.length === 0) {
    return null
  }

  // A configured address is a template token (`{{contactEmail}}`), so it must
  // not be shown until resolved
  if (isPending) {
    return <Skeleton width={SKELETON_WIDTH} height={SKELETON_HEIGHT} />
  }

  return (
    <Text
      {...Primary13Regular}
      color={GreyGrey60}
      wordBreak="break-all"
      data-testid="automations.executionSummary.reminder.emailRecipients"
    >
      {to.map(resolveTemplate).join(', ')}
    </Text>
  )
}
